import { useState } from "react";
import { X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { IDocumentoEnhanced } from "@/types/documento";
import { cn } from "@/lib/class-control";

interface DocumentViewModalProps {
  document: IDocumentoEnhanced | null;
  open: boolean;
  onClose: () => void;
}

/**
 * DocumentViewModal Component
 *
 * Enhanced modal for viewing documents with two-layer card design and multi-page support.
 * Features thumbnail strip for multi-page PDFs with page navigation.
 *
 * @param document - Document to display with enhanced metadata and optional pages
 * @param open - Whether the modal is open
 * @param onClose - Callback to close the modal
 *
 * @example
 * ```tsx
 * <DocumentViewModal
 *   document={selectedDocument}
 *   open={modalOpen}
 *   onClose={() => setModalOpen(false)}
 * />
 * ```
 */
export function DocumentViewModal({
  document,
  open,
  onClose,
}: DocumentViewModalProps) {
  const [activePage, setActivePage] = useState(1);

  if (!document) return null;

  const isPDF = document.fileType.toLowerCase() === "pdf";
  const isImage = ["jpg", "jpeg", "png", "gif", "webp"].includes(
    document.fileType.toLowerCase()
  );
  const hasPages = document.pages && document.pages.length > 0;
  const currentPageData = hasPages && document.pages
    ? document.pages.find((p) => p.pageNumber === activePage)
    : null;

  const displayUrl = currentPageData?.fullSizeUrl || document.fileUrl;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#15203C] border-[#5D5D5D] w-[800px] max-w-[800px] p-[30px] flex flex-col">
        {/* Inner Card - Two-Layer Design */}
        <div className="bg-[#0C1427] rounded-[20px] p-[30px] flex flex-col h-full">
          {/* Header */}
          <DialogHeader className="flex flex-row items-start justify-between mb-[20px]">
            <div className="flex-1">
              <DialogTitle className="text-[24px] font-bold text-white font-['Plus_Jakarta_Sans']">
                {document.fileName}
              </DialogTitle>
              <p className="text-[14px] text-[#D1D1D1] font-['Plus_Jakarta_Sans'] mt-[8px]">
                Responsável: {document.responsible.name} • Data: {document.date}
              </p>
              {document.description && (
                <p className="text-[14px] text-[#F6F6F6] font-['Plus_Jakarta_Sans'] mt-[10px]">
                  {document.description}
                </p>
              )}
            </div>
            <button
              onClick={onClose}
              className="hover:opacity-70 transition-opacity ml-[20px]"
              aria-label="Fechar modal"
            >
              <X className="size-[24px] text-white" />
            </button>
          </DialogHeader>

          {/* Document Preview */}
          <div className="flex-1 overflow-auto mb-[20px]">
            <div className="h-[801px] rounded-[12px] overflow-hidden">
              {isPDF && (
                <iframe
                  src={displayUrl}
                  className="w-full h-full bg-white"
                  title={document.fileName}
                />
              )}
              {isImage && (
                <img
                  src={displayUrl}
                  alt={document.fileName}
                  className="w-full h-auto rounded-[12px]"
                />
              )}
              {!isPDF && !isImage && (
                <div className="flex items-center justify-center h-full">
                  <p className="text-[#888888] text-[14px] font-['Plus_Jakarta_Sans']">
                    Prévia não disponível para este tipo de arquivo
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Multi-page thumbnail strip */}
          {hasPages && document.pages!.length > 1 && (
            <div className="border-t border-[#5D5D5D]/30 pt-[20px]">
              <div className="h-[175px] flex items-center gap-[12px] overflow-x-auto scrollbar-thin scrollbar-thumb-[#5D5D5D] scrollbar-track-[#0C1427]">
                {document.pages!.map((page) => (
                  <button
                    key={page.pageNumber}
                    onClick={() => setActivePage(page.pageNumber)}
                    className={cn(
                      "relative flex-shrink-0 w-[100px] h-[120px] rounded-[8px] border-2 overflow-hidden transition-all",
                      activePage === page.pageNumber
                        ? "border-[#7846FF] ring-2 ring-[#7846FF]/50"
                        : "border-[#5D5D5D] hover:border-[#7846FF]/50"
                    )}
                    aria-label={`Página ${page.pageNumber}`}
                  >
                    <img
                      src={page.thumbnailUrl}
                      alt={`Página ${page.pageNumber}`}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] text-center py-[2px] font-['Plus_Jakarta_Sans']">
                      Página {page.pageNumber}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
